import { useContext, useEffect, useRef } from 'react';

import { PartnerGMetrixTableDialog, PartnerGMetrixTableDialogHandle } from 'components/PartnerGMetrixTableDialog/PartnerGMetrixTableDialog';
import { StudentGMetrixTableDialog, StudentGMetrixTableDialogHandle } from 'components/StudentGMetrixTableDialog/StudentGMetrixTableDialog';
import { useReportCatalog, useReportUrlState, useScopeResolver } from 'pages/reports/hooks';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { ReportsDefaultContext } from 'pages/reports/context/ReportsDefaultContext';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { CategoryReport, Report, ReportApi, ResultItem } from 'apis/ReportApi';
import { ReportCustomModal } from 'pages/reports/components/CustomReportModal';
import { ReportsLanding } from 'pages/reports/components/ReportsLanding';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { ReportViewer } from 'pages/reports/components/ReportViewer';
import { useNotification } from 'app/notifications';
import { Container, Row } from 'react-bootstrap';
import { KendoIntlProvider } from 'app/culture';
import { PartnerTypes } from 'apis/PartnerApi';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';
import { useApp } from 'app';

import 'pages/reports/styles/main.scss';
import 'styles/global-styles.scss';
import 'styles/kendo-default-theme-with-overrides.scss';

/* ======================================================
   TYPES
====================================================== */

export interface ChartData {
    data: ResultItem[];
    dataType: any;
}

/* ======================================================
   MAIN COMPONENT
====================================================== */

export const Reports = () => {

    const { cultureId, highestRole: appHighestRole, selectedPartner: appSelectedPartner } = useApp();
    const { permissions } = useContext(ReportsDefaultContext);

    const highestRole = { Id: appHighestRole.id, Name: appHighestRole.name };
    const selectedPartner = { Id: appSelectedPartner.id, Name: appSelectedPartner.name, Type: appSelectedPartner.type };

    const { formatMessage, locale } = useIntl();

    const { clearReport, reportId, scopeId, scopeObjectId, scopeObjectName, setParams, testName } = useReportUrlState();
    const { customReports, listOfReports, reload: reloadCatalog } = useReportCatalog();
    const { createToast } = useNotification();
    const intl = useIntl();
    console.log(listOfReports);
    const [state, setState] = useSuperState({
        currentlySelectedReport: undefined as Report | undefined,
        customDescription: '',
        customName: '',
        editingReport: undefined as Report | undefined,
        relatedReports: [] as Report[],
        scopeObject: undefined as { Id: number; Name: string } | undefined,
        selectedCategory: undefined as CategoryReport | undefined,
        showCustomModal: false,
        studentCompletionDate: null as string | null
    });
    const confirmRef = useRef<any>();
    const partnerPickerRef = useRef<PartnerGMetrixTableDialogHandle>(null);
    const studentPickerRef = useRef<StudentGMetrixTableDialogHandle>(null);
    const { resolveScope } = useScopeResolver({ partnerPickerRef, studentPickerRef });

    const {
        currentlySelectedReport, customDescription, customName,
        editingReport, relatedReports, scopeObject,
        selectedCategory, showCustomModal, studentCompletionDate
    } = state;

    /* ======================================================
       EFFECTS
    ====================================================== */

    useEffect(() => {

        const savedCategory = sessionStorage.getItem('selectedCategory');
        if (savedCategory) setState({ selectedCategory: JSON.parse(savedCategory) });

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {

        sessionStorage.setItem(
            'selectedCategory',
            selectedCategory ? JSON.stringify(selectedCategory) : ''
        );

    }, [selectedCategory]);

    // Restore the selected report from the URL. A deep-linked report id is trusted:
    // we go straight to the report layout without waiting for or requiring the
    // "available reports" catalog. If the catalog is loaded we prefer its richer
    // Report entry (Name, filters); otherwise we build a minimal report from the URL.
    useEffect(() => {

        if (!reportId) return;
        if (currentlySelectedReport && currentlySelectedReport.Id === reportId) return;

        const allReports: Report[] = [
            ...listOfReports.flatMap(c => c.Reports),
            ...customReports.map((r: any) => ({ ...r, Id: r.ReportId }))
        ];
        const report = allReports.find(r => r.Id === reportId)
            ?? ({ Id: reportId, ScopeId: scopeId ?? 0 } as Report);

        // Hydrate scopeObject from URL if present so the viewer can render.
        if (scopeObjectId && !scopeObject) {

            setState({
                scopeObject: {
                    Id: scopeObjectId,
                    Name: scopeObjectName ?? PartnerTypes[scopeId ?? report.ScopeId ?? 0] ?? 'Scope'
                }
            });

        }

        selectReport(report, { skipUrlWrite: true });

    }, [reportId, listOfReports, customReports]); // eslint-disable-line react-hooks/exhaustive-deps

    // When the report id is cleared from the URL (e.g. the user clicks the
    // Reports item in the side menu while viewing a report), fall back to the
    // report picker instead of leaving the previously-selected report on screen.
    const prevReportIdRef = useRef<number | null>(reportId);
    useEffect(() => {

        const previousReportId = prevReportIdRef.current;
        prevReportIdRef.current = reportId;

        if (previousReportId && !reportId && (currentlySelectedReport || scopeObject)) {

            setState({
                currentlySelectedReport: undefined,
                relatedReports: [],
                scopeObject: undefined,
                studentCompletionDate: null
            });

        }

    }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ======================================================
       REPORT SELECTION
    ====================================================== */

    const selectReport = async (report: Report, options?: { forceRescope?: boolean; skipUrlWrite?: boolean }) => {

        setState({
            currentlySelectedReport: report,
            studentCompletionDate: null
        });

        const implicitScopeChange = !options?.skipUrlWrite
            && !!scopeId
            && !!report.ScopeId
            && report.ScopeId !== scopeId;
        if (implicitScopeChange) {

            setState({ scopeObject: undefined });
            setParams({ ScopeObjectId: undefined, ScopeObjectName: undefined });

        }
        const forceRescope = options?.forceRescope || implicitScopeChange;

        let effectiveScope = implicitScopeChange ? undefined : scopeObject;
        if (!forceRescope && !effectiveScope && scopeObjectId && scopeId === report.ScopeId) {

            effectiveScope = {
                Id: scopeObjectId,
                Name: scopeObjectName ?? PartnerTypes[scopeId] ?? 'Scope'
            };
            setState({ scopeObject: effectiveScope });

        }

        // Reuse the existing scope when the ScopeId matches (skip the picker).
        const urlScopeAlreadyMatches = !forceRescope && effectiveScope && report.ScopeId === scopeId;
        const needsResolve = !!(report.ScopeId && (forceRescope || !effectiveScope));

        if (urlScopeAlreadyMatches) {

            if (!options?.skipUrlWrite) {

                setParams({ ReportId: report.Id, ScopeId: report.ScopeId });

            }

        } else if (needsResolve) {

            const resolved = await resolveScope({ flavor: 'select', highestRoleId: highestRole.Id, scopeId: report.ScopeId, selectedPartner });

            if (!resolved) {

                // User cancelled the picker — roll back.
                setState({ currentlySelectedReport: undefined, scopeObject: undefined });
                if (!options?.skipUrlWrite) {

                    setParams({ ReportId: undefined, ScopeId: undefined });

                }
                return;

            }

            // Commit scope to state first so the viewer renders with a consistent
            // (report, scope) pair. URL sync is for bookmarkability only.
            setState({ scopeObject: { Id: resolved.Id, Name: resolved.Name } });

            if (!options?.skipUrlWrite) {

                setParams({
                    ReportId: report.Id,
                    ScopeId: report.ScopeId,
                    ScopeObjectId: resolved.Id,
                    ScopeObjectName: resolved.Name
                });

            } else {

                setParams({ ScopeObjectId: resolved.Id, ScopeObjectName: resolved.Name });

            }

        } else if (!options?.skipUrlWrite) {

            setParams({ ReportId: report.Id, ScopeId: report.ScopeId });

        }

    };

    /** Open a saved custom report by parsing its stored URL. */
    const customReportSelectOnClick = (report: any) => {

        const parsed = new URLSearchParams(report.URL);
        setParams({
            ReportId: report.ReportId ?? parsed.get('ReportId') ?? undefined,
            ScopeId: parsed.get('ScopeId') ?? undefined,
            ScopeObjectId: parsed.get('ScopeObjectId') ?? undefined,
            ScopeObjectName: parsed.get('ScopeObjectName') ?? undefined,
            TestName: parsed.get('TestName') ?? undefined
        });

        // Normalize so the viewer sees Id (not ReportId) and a numeric ScopeId.
        const parsedScopeId = parsed.get('ScopeId');
        const parsedScopeObjectId = parsed.get('ScopeObjectId');
        const parsedScopeObjectName = parsed.get('ScopeObjectName');
        const normalized: Report = {
            ...report,
            Id: report.ReportId,
            ScopeId: parsedScopeId ? Number(parsedScopeId) : report.ScopeId
        };

        setState({
            currentlySelectedReport: normalized,
            scopeObject: parsedScopeObjectId ? {
                Id: Number(parsedScopeObjectId),
                Name: parsedScopeObjectName ?? PartnerTypes[Number(parsedScopeId) || 0] ?? 'Scope'
            } : undefined
        });

    };

    const reportSelectOnClick = (report: Report) => {

        selectReport(report);

    };

    /* ======================================================
       CUSTOM REPORTS
    ====================================================== */

    const saveCustomReport = async () => {

        if (!reportId) {

            createToast(
                formatMessage({ id: 'no_report_has_been_chosen.291ccec2', defaultMessage: 'No Report Has been Chosen' }),
                'danger'
            );
            return;

        }

        if (!customName || !customDescription) return;

        const urlParams = window.location.href.split('?')[1] ?? '';

        const success = await ReportApi.CreateUpdateCustomReport(
            0,
            urlParams,
            reportId,
            customName,
            customDescription
        );

        createToast(
            success
                ? (formatMessage({ id: 'successfully_saved_report.dacaad87', defaultMessage: 'Successfully saved report' }))
                : (formatMessage({ id: 'failed_saving_your_report.80c3fcce', defaultMessage: 'Failed saving your report' })),
            success ? 'success' : 'danger'
        );

        if (success) {

            await reloadCatalog();

        }

        closeCustomModal();

    };

    const editCustomReport = async () => {

        if (!editingReport || !customName || !customDescription) return;

        const result = await ReportApi.CreateUpdateCustomReport(
            editingReport.Id,
            editingReport.URL,
            editingReport.ReportId,
            customName,
            customDescription,
            cultureId
        );

        createToast(
            result.Done
                ? (formatMessage({ id: 'custom_report_edited_successfully.40add028', defaultMessage: 'Custom report edited successfully' }))
                : (formatMessage({ id: 'custom_report_failed_to_save.18534178', defaultMessage: 'Custom report failed to save' })),
            result.Done ? 'success' : 'danger'
        );

        if (result.Done) {

            await reloadCatalog();

        }

        closeCustomModal();

    };

    const closeCustomModal = () => {

        setState({ customDescription: '', customName: '', editingReport: undefined, showCustomModal: false });

    };

    /* ======================================================
       RENDER HELPERS
    ====================================================== */

    const printModal = () => {

        const reportRoot = document.getElementById('root');
        if (!reportRoot || !reportRoot.innerText.trim()) {

            createToast(formatMessage({ id: 'report_content_is_not_ready_to_print_yet.c84a96f6', defaultMessage: 'Report content is not ready to print yet.' }), 'warning');
            return;

        }

        let printScope = reportRoot as HTMLElement;
        while (printScope.parentElement && printScope.parentElement !== document.body) {

            printScope = printScope.parentElement as HTMLElement;

        }

        const originalScopeRole = printScope.getAttribute('role');
        const originalScopeFlag = printScope.getAttribute('data-reports-print-scope');
        printScope.setAttribute('role', 'dialog');
        printScope.setAttribute('data-reports-print-scope', 'true');

        const printOverrideStyle = document.createElement('style');
        printOverrideStyle.setAttribute('data-print-override', 'reports-modal-print');
        printOverrideStyle.textContent = `
            @media print {
                html, body {
                    height: auto !important;
                    overflow: visible !important;
                }
                body > *:not([data-reports-print-scope="true"]) {
                    display: none !important;
                }
                [data-reports-print-scope="true"] {
                    display: block !important;
                    visibility: visible !important;
                }
                [data-reports-print-scope="true"] * {
                    visibility: visible !important;
                }
            }
        `;
        (document.head ?? document.body).appendChild(printOverrideStyle);

        const cleanup = () => {

            printOverrideStyle.remove();

            if (originalScopeRole === null) {

                printScope.removeAttribute('role');

            } else {

                printScope.setAttribute('role', originalScopeRole);

            }

            if (originalScopeFlag === null) {

                printScope.removeAttribute('data-reports-print-scope');

            } else {

                printScope.setAttribute('data-reports-print-scope', originalScopeFlag);

            }

        };

        window.addEventListener('afterprint', cleanup, { once: true });
        setTimeout(cleanup, 5000);
        window.focus();
        window.print();

    };

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>
                    <GMetrixDialogConfirm ref={confirmRef} />

                    <Row>
                        {currentlySelectedReport && scopeObject ? (
                            <ReportViewer
                                onBack={() => {

                                    clearReport();
                                    setState({
                                        currentlySelectedReport: undefined,
                                        relatedReports: [],
                                        scopeObject: undefined,
                                        studentCompletionDate: null
                                    });

                                }}
                                onChangeScope={async () => {

                                    const resolved = await resolveScope({
                                        flavor: 'change',
                                        highestRoleId: highestRole.Id,
                                        scopeId: currentlySelectedReport.ScopeId,
                                        selectedPartner
                                    });
                                    if (!resolved) return;
                                    setState({ scopeObject: { Id: resolved.Id, Name: resolved.Name } });
                                    setParams({ ScopeObjectId: resolved.Id, ScopeObjectName: resolved.Name });

                                }}
                                onReportLoaded={(report: Report) => {

                                    setState({ relatedReports: (report as any).RelatedReports || [] });

                                    // A deep-linked report may have been launched from a URL-only stub
                                    // (no Name yet). Backfill display fields from the loaded layout so
                                    // the viewer shows the correct title and scope.
                                    if (report && (!currentlySelectedReport.Name || currentlySelectedReport.Name !== report.Name)) {

                                        setState({
                                            currentlySelectedReport: {
                                                ...currentlySelectedReport,
                                                Name: report.Name ?? currentlySelectedReport.Name,
                                                ScopeId: report.ScopeId ?? currentlySelectedReport.ScopeId
                                            }
                                        });

                                    }

                                    // Extract completion date for student reports
                                    if (currentlySelectedReport.ScopeId === 8 || currentlySelectedReport.ScopeId === 10) {

                                        const data = report.ReportSections?.[0]?.Components?.[0]?.Results?.Data;
                                        if (data && data.length > 0) {

                                            const completionDate = data[0]?.CourseDateCompleted;
                                            if (completionDate) {

                                                setState({ studentCompletionDate: intl.formatDate(new Date(completionDate), { day: 'numeric', month: 'long', year: 'numeric' }) });

                                            } else {

                                                setState({ studentCompletionDate: null });

                                            }

                                        }

                                    }

                                }}
                                cultureId={cultureId}
                                currentlySelectedReport={currentlySelectedReport}
                                highestRole={highestRole}
                                listOfReports={listOfReports}
                                onPrint={printModal}
                                onSelectRelatedReport={selectReport}
                                permissions={permissions}
                                relatedReports={relatedReports}
                                scopeObject={scopeObject}
                                selectedPartner={selectedPartner}
                                studentCompletionDate={studentCompletionDate}
                                testName={testName}
                            />
                        ) : listOfReports.length ? (
                            <ReportsLanding
                                onEditCustomReport={report => setState({
                                    customDescription: report.Description,
                                    customName: report.Name,
                                    editingReport: report,
                                    showCustomModal: true
                                })}
                                confirmRef={confirmRef}
                                customReports={customReports}
                                isSuperAdmin={highestRole.Id === 1}
                                listOfReports={listOfReports}
                                onCustomReportDeleted={reloadCatalog}
                                onCustomReportSelect={customReportSelectOnClick}
                                onReportSelect={reportSelectOnClick}
                            />
                        ) : (
                            <Container className="d-flex justify-content-center">
                                <GMetrixLoader />
                            </Container>
                        )}
                    </Row>

                    <PartnerGMetrixTableDialog
                        multiselect={false}
                        option="Change"
                        ref={partnerPickerRef}
                    />

                    <StudentGMetrixTableDialog
                        studentAssociation={{
                            associationIds: [] as number[],
                            associationName: 'Report'
                        }}
                        multiSelect={false}
                        option="Add"
                        permissions={permissions}
                        ref={studentPickerRef}
                    />

                    <ReportCustomModal
                        description={customDescription}
                        isEditing={!!editingReport}
                        name={customName}
                        onCancel={closeCustomModal}
                        onDescriptionChange={description => setState({ customDescription: description })}
                        onNameChange={name => setState({ customName: name })}
                        onSave={() => editingReport ? editCustomReport() : saveCustomReport()}
                        show={showCustomModal}
                    />
                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>
    );

};
